/** @file filterScanlines: PNG-filters RGBA8 rows (none, sub, up, or adaptive per row) for encoding. */
import type { Image } from '../types/image';

/** `'adaptive'` picks, per row, the smallest sum of |signed byte| among none, sub and up. */
export type PngFilterMode = 'none' | 'sub' | 'up' | 'adaptive';

const BPP = 4;

/** Writes row `y` filtered with type `f` (0 none, 1 sub, 2 up) into `out` at `o`, filter byte first; returns Σ|signed byte|. */
function filterRow(data: Uint8ClampedArray, y: number, rowBytes: number, f: 0 | 1 | 2, out: Uint8Array, o: number): number {
  const row = y * rowBytes;
  let cost = 0;
  out[o] = f;
  for (let i = 0; i < rowBytes; i++) {
    const pred = f === 1 ? (i >= BPP ? data[row + i - BPP] ?? 0 : 0) : f === 2 && y > 0 ? data[row - rowBytes + i] ?? 0 : 0;
    const v = ((data[row + i] ?? 0) - pred) & 0xff;
    out[o + 1 + i] = v;
    cost += v < 128 ? v : 256 - v;
  }
  return cost;
}

/** The filtered scanlines of `image` (`height * (1 + width * 4)` bytes), ready for deflate. */
export function filterScanlines(image: Image, mode: PngFilterMode): Uint8Array {
  const rowBytes = image.width * BPP;
  const out = new Uint8Array(image.height * (rowBytes + 1));
  const fixed = { none: 0, sub: 1, up: 2 } as const;
  const scratch = new Uint8Array(rowBytes + 1);
  for (let y = 0; y < image.height; y++) {
    const o = y * (rowBytes + 1);
    if (mode !== 'adaptive') {
      filterRow(image.data, y, rowBytes, fixed[mode], out, o);
      continue;
    }
    let best = filterRow(image.data, y, rowBytes, 0, out, o);
    for (const f of [1, 2] as const) {
      const cost = filterRow(image.data, y, rowBytes, f, scratch, 0);
      if (cost < best) {
        best = cost;
        out.set(scratch, o);
      }
    }
  }
  return out;
}
